// [src/utils/iChing/calendar.js] [2024-05-24 21:15:00]
// 功能：中国历法通用计算模块 - 最终版
// 目的：精确获取用户输入时间的前后节令和气令信息，并集成四柱空亡计算

// ====== 导入历法库 ======
import { Lunar, Solar } from '../../services/lunar.js';

// ==================== 旬空表（六十甲子） ====================
 const XUN_TABLE = [
  { leader: '甲子', kong: '戌亥' },
  { leader: '甲戌', kong: '申酉' },
  { leader: '甲申', kong: '午未' },
  { leader: '甲午', kong: '辰巳' },
  { leader: '甲辰', kong: '寅卯' },
  { leader: '甲寅', kong: '子丑' }
];

// ==================== 天干索引表 ====================
const TIAN_GAN_INDEX = '甲乙丙丁戊己庚辛壬癸';

// ==================== 地支索引表 ====================
const DI_ZHI_INDEX = '子丑寅卯辰巳午未申酉戌亥';

/**
 * 根据任意干支查询其所在旬及旬空
 * @param {string} ganZhi - 需要查询的干支，例如 "甲子", "丙寅"
 * @returns {object} 包含旬和旬空信息的对象 { xun, kongWang } 或 null
 */
export function getKongWangByGanZhi(ganZhi) {
  // --- 1. 输入验证 ---
  if (!ganZhi || typeof ganZhi !== 'string' || ganZhi.length !== 3) {
    return null;
  }

  // --- 2. 解析干支 ---
  const targetGan = ganZhi.substring(0, 1); // 获取天干，如 "甲"
  const targetZhi = ganZhi.substring(1, 3); // 获取地支，如 "子"

  // --- 3. 获取干支索引 ---
  const ganIndex = TIAN_GAN_INDEX.indexOf(targetGan);
  const zhiIndex = DI_ZHI_INDEX.indexOf(targetZhi);

  // --- 4. 计算干支的绝对位置 (0-59) ---
  const index = (ganIndex * 6 + zhiIndex) % 60;

  // --- 5. 计算所在旬的领头干支的索引 ---
  const xunLeaderIndex = Math.floor(index / 10) * 10;

  // --- 6. 从六十甲子表中获取旬的领头干支 ---
  const sixtyJiaZi = '甲子乙丑丙寅丁卯戊辰己巳庚午辛未壬申癸酉戌亥'.split('');
  const xunLeader = sixtyJiaZi[xunLeaderIndex];

  // --- 7. 在我们定义的旬表中查找对应的旬空 ---
  const xunInfo = XUN_TABLE.find(item => item.leader === xunLeader);

  return xunInfo ? { xun: `${xunLeader}旬`, kongWang: xunInfo.kong } : null;
}

/**
 * 获取精确的节令和气令信息（专门用于六爻排盘）
 * @param {string} datetimeStr - 公历时间字符串，格式 "YYYYMMDDHHmmss"
 * @returns {object} 包含所有节令和气令信息的对象
 */
export function getJieQiInfo(datetimeStr) {
  // --- 1. 解析时间并创建 Lunar 对象 ---
  const year = parseInt(datetimeStr.substring(0, 4));
  const month = parseInt(datetimeStr.substring(4, 6));
  const day = parseInt(datetimeStr.substring(6, 8));
  const hour = parseInt(datetimeStr.substring(8, 10));
  const minute = parseInt(datetimeStr.substring(10, 12));
  const second = parseInt(datetimeStr.substring(12, 14));

  // 使用 Solar 和 Lunar 对象来表示当前时间
  const solar = Solar.fromYmdHms(year, month, day, hour, minute, second);
  const lunar = solar.getLunar();

  // --- 2. 获取所有节令和气令信息（精确时刻匹配）---
  // 使用 false 参数进行精确时刻匹配
  const prevJie = lunar.getPrevJie(false);      // 前一个节令（精确）
  const nextJie = lunar.getNextJie(false);      // 后一个节令（精确）
  const prevQi = lunar.getPrevQi(false);        // 前一个气令（精确）
  const nextQi = lunar.getNextQi(false);        // 后一个气令（精确）
  const currentJie = lunar.getCurrentJie();     // 当前节令（当天）
  const currentQi = lunar.getCurrentQi();       // 当前气令（当天）

  // --- 3. 格式化节令和气令信息 ---
  // 【关键修改】使用更可靠的方法格式化日期，确保输出为 "YYYY年MM月DD日 HH:mm:ss"
  const formatJieQi = (jieqi, positionTag) => {
    if (!jieqi) return null;
    const solarDate = jieqi.getSolar();

    // 直接从Solar对象获取年、月、日、时、分、秒
    const jieqiYear = solarDate.getYear();
    const jieqiMonth = solarDate.getMonth();
    const jieqiDay = solarDate.getDay();
    const jieqiHour = solarDate.getHour();
    const jieqiMinute = solarDate.getMinute();
    const jieqiSecond = solarDate.getSecond();

    // 【再次修改】对月、日、时也进行补位，确保格式统一
    const formattedDate = `${jieqiYear}年${String(jieqiMonth).padStart(2, '0')}月${String(jieqiDay).padStart(2, '0')}日 ${String(jieqiHour).padStart(2, '0')}:${String(jieqiMinute).padStart(2, '0')}:${String(jieqiSecond).padStart(2, '0')}`;

    return {
      name: jieqi.getName(),
      solar_date: formattedDate, // <--- 使用手动构建的格式
      type: jieqi.isJie() ? '节令' : (jieqi.isQi() ? '气令' : '节气'),
      timestamp: solarDate.toYmdHms(), // <--- timestamp 保留原始格式，便于排序
      position: positionTag
    };
  };

  // --- 4. 【关键修正】精确计算“前一个节令的后一个气令” ---
  let prevJieNextQiFormatted = null;
  if (prevJie) {
    // 以前一个节令的精确时间为基准，创建新的 Lunar 对象
    const prevJieSolar = prevJie.getSolar();
    const prevJieLunar = prevJieSolar.getLunar();
    // 基于这个新的时间点，计算其后的第一个气令
    const prevJieNextQi = prevJieLunar.getNextQi(false);
    prevJieNextQiFormatted = formatJieQi(prevJieNextQi, 'prev_jie_next_qi');
  }

  // --- 5. 构建返回结果 ---
  return {
    // 节令信息
    jie: {
      previous: formatJieQi(prevJie, 'prev_jie'),      // 前一个节令
      next: formatJieQi(nextJie, 'next_jie'),          // 后一个节令
      current: formatJieQi(currentJie, 'current_jie'),    // 当前节令
      // 【关键新增】前一个节令的后一个气令（精确计算）
      prev_next_qi: prevJieNextQiFormatted
    },
    // 气令信息
    qi: {
      previous: formatJieQi(prevQi, 'prev_qi'),       // 前一个气令
      next: formatJieQi(nextQi, 'next_qi'),           // 后一个气令
      current: formatJieQi(currentQi, 'current_qi')      // 当前气令
    },
    // 所有节令气令按时间顺序排列，并携带位置标识
    all_jieqi: [
      formatJieQi(prevJie, 'prev_jie'),
      formatJieQi(prevQi, 'prev_qi'),
      formatJieQi(currentJie, 'current_jie'),
      formatJieQi(currentQi, 'current_qi'),
      formatJieQi(nextQi, 'next_qi'),
      formatJieQi(nextJie, 'next_jie')
    ].filter(item => item !== null).sort((a, b) => a.timestamp.localeCompare(b.timestamp))
  };
}

/**
 * 根据公历时间字符串计算农历和干支信息
 * @param {string} datetimeStr - 公历时间字符串，格式 "YYYYMMDDHHmmss"
 * @param {boolean} splitZiHour - 是否使用早晚子时
 * @param {number} dayZhiType - 日干支计算流派 (1: 23点换日, 2: 00点换日)
 * @returns {object} 包含农历、干支等信息的时间对象
 */
export function calculateCalendarInfo(datetimeStr, splitZiHour, dayZhiType = 1) {
  // --- 1. 解析时间字符串 ---
  const year = parseInt(datetimeStr.substring(0, 4));
  const month = parseInt(datetimeStr.substring(4, 6));
  const day = parseInt(datetimeStr.substring(6, 8));
  const hour = parseInt(datetimeStr.substring(8, 10));
  const minute = parseInt(datetimeStr.substring(10, 12));
  const second = parseInt(datetimeStr.substring(12, 14));

  // --- 2. 直接使用原始时间创建公历 (Solar) 对象 ---
  const solar = Solar.fromYmdHms(year, month, day, hour, minute, second);

  // --- 3. 转换为农历 (Lunar) 对象 ---
  const lunar = solar.getLunar();

  // --- 4. 格式化输出 ---
  const formattedSolarDate = `${year}年${month}月${day}日 ${hour}:${String(minute).padStart(2, '0')}:${String(second).padStart(2, '0')}`;

  // --- 5. 构建带早晚子时的农历字符串 ---
  let ziHourPrefix = '';
  const timeZhi = lunar.getTimeZhi();
  if (timeZhi === '子') {
    if (hour >= 23) {
      ziHourPrefix = '晚';
    } else { // hour >= 0 && hour < 2
      ziHourPrefix = '早';
    }
  }

  const formattedLunarDate = `${lunar.getYearInGanZhi()}${lunar.getShengxiao()}年(${lunar.getYearNaYin()})${lunar.getMonthInChinese()}月${lunar.getDayInChinese()}${ziHourPrefix}${timeZhi}时`;

  // --- 6. 获取节令气令信息 ---
  const jieqiInfo = getJieQiInfo(datetimeStr);

  // --- 7. 【关键修改】精确计算干支信息 ---
  // 年干支：以立春交接的精确时间为分界
  const yearGanZhi = lunar.getYearInGanZhiExact();
  // 月干支：以交节令的精确时间为分界
  const monthGanZhi = lunar.getMonthInGanZhiExact();
  // 日干支：根据流派计算
  let dayGanZhi = '';
  if (dayZhiType === 2) {
    dayGanZhi = lunar.getDayInGanZhiExact2(); // 流派2：以00:00为界
  } else {
    dayGanZhi = lunar.getDayInGanZhiExact();  // 流派1：以23:00为界
  }
  // 时干支：保持不变
  const timeGanZhi = lunar.getTimeInGanZhi();

  // --- 7.1. 【新增】计算四柱空亡 ---
  // 年柱空亡
  const yearKong = lunar.getYearXunKongExact();
  // 月柱空亡
  const monthKong = lunar.getMonthXunKongExact();
  // 日柱空亡
  const dayKong = lunar.getDayXunKongExact();
  // 时柱空亡
  const timeKong = lunar.getTimeXunKong();

  // --- 8. 构建返回结果 ---
  return {
    solar_date: formattedSolarDate,
    lunar_date: formattedLunarDate,
    jieqi: jieqiInfo, // <--- 包含所有节令气令信息
    ganzhi: {
      year: yearGanZhi,
      month: monthGanZhi,
      day: dayGanZhi,
      hour: timeGanZhi,
      // 【新增】返回日干支计算类型，便于调试
      day_zhi_type: dayZhiType,
      // 【新增】返回四柱空亡信息
      kong_wang: {
        year: yearKong,
        month: monthKong,
        day: dayKong,
        time: timeKong
      }
    }
  };
}